import React, { useState, useRef, useCallback } from 'react';
import {
  FiUpload, FiEdit3, FiZap, FiCheckCircle, FiMail, FiDownload,
  FiX, FiChevronDown, FiChevronUp, FiFile, FiAlertCircle, FiRefreshCw,
  FiXCircle, FiSend,
} from 'react-icons/fi';
import { uploadInvoice, saveInvoiceReview, pushInvoiceToSap } from '../../utils/ocrApi';

const STEPS = ['Upload Invoices', 'Review & Edit', 'Actions'];

// ── Helpers ──────────────────────────────────────────────────────────────────
function flattenExtracted(extractedData) {
  const rows = [];
  if (!Array.isArray(extractedData)) return rows;
  extractedData.forEach((arr, ii) => {
    if (Array.isArray(arr)) {
      arr.forEach((inv, di) => {
        const { Items, items, ...rest } = inv;
        rows.push({ ...rest, _items: Items || items || [], _id: `${ii}-${di}` });
      });
    }
  });
  return rows;
}

function buildEditRow(inv) {
  return {
    invoice_number:        inv['Invoice Number']   || inv['invoice_number']        || inv['InvoiceNumber']  || '',
    invoice_date:          inv['Invoice Date']     || inv['invoice_date']           || inv['Date']           || '',
    invoice_total:         inv['Invoice Total']    || inv['invoice_total']          || inv['Total Amount']   || inv['Total'] || '',
    purchase_order_number: inv['PO Number']        || inv['purchase_order_number']  || inv['PO No']          || '',
    ap_processor:          inv['AP Processor']     || inv['ap_processor']           || localStorage.getItem('userEmail') || '',
  };
}

function displayKeys(inv) {
  return Object.keys(inv).filter((k) => !k.startsWith('_'));
}

function fileColor(ext) {
  const e = ext.toLowerCase();
  if (e === 'pdf') return '#e53e3e';
  if (['png','jpg','jpeg'].includes(e)) return '#6b46c1';
  if (['xlsx','xls'].includes(e)) return '#2f855a';
  return '#4a5568';
}

function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/(1024*1024)).toFixed(1)} MB`;
}

// ── Main Component ────────────────────────────────────────────────────────────
const OCRProcess = ({ onUploadComplete }) => {
  const [step,         setStep]         = useState(0);
  const [dragOver,     setDragOver]     = useState(false);
  const [files,        setFiles]        = useState([]);
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState('');
  const [uploadStage,  setUploadStage]  = useState('idle'); // 'idle'|'uploading'|'processing'|'done'

  const [extractedRows, setExtractedRows] = useState([]);
  const [editedRows,    setEditedRows]    = useState([]);
  const [activeInvIdx,  setActiveInvIdx]  = useState(0);
  const [expandedIds,   setExpandedIds]   = useState(new Set());
  const [savingIdx,     setSavingIdx]     = useState(null);
  const [savedIdx,      setSavedIdx]      = useState([]);
  const [actions,       setActions]       = useState({});
  const [summary,       setSummary]       = useState(null);

  const fileRef = useRef(null);

  // ── File management ──────────────────────────────────────────────────────────
  const addFiles = (incoming) => {
    if (!incoming?.length) return;
    setUploadError('');
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...Array.from(incoming).filter((f) => !existing.has(f.name))];
    });
  };
  const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.name !== name));

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  // ── Upload ──────────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!files.length) return;
    setUploading(true); setUploadError(''); setUploadStage('uploading');
    try {
      setUploadStage('processing');
      const data = await uploadInvoice(files);
      setUploadStage('done');

      let extracted = [];
      if (data.results?.extracted_data) extracted = data.results.extracted_data;
      else if (Array.isArray(data))      extracted = data;
      else if (data.extracted_data)      extracted = data.extracted_data;

      if (data.results) setSummary(data.results);

      const flat = flattenExtracted(extracted);
      setExtractedRows(flat);
      setEditedRows(flat.map(buildEditRow));
      setExpandedIds(new Set(flat.map((r) => r._id)));
      setActiveInvIdx(0);
      setSavedIdx([]); setActions({});

      const invoiceNums = flat
        .map((r) => r['Invoice Number'] || r['invoice_number'] || r['InvoiceNumber'] || '')
        .filter(Boolean);
      if (onUploadComplete) onUploadComplete(invoiceNums);

      setStep(1);
    } catch (err) {
      setUploadError(err.message);
      setUploadStage('idle');
    } finally {
      setUploading(false);
    }
  }, [files, onUploadComplete]);

  // ── Edit ─────────────────────────────────────────────────────────────────────
  const updateField = (idx, field, value) =>
    setEditedRows((prev) => { const c = [...prev]; c[idx] = { ...c[idx], [field]: value }; return c; });

  const toggleExpand = (id) =>
    setExpandedIds((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleSave = async (idx) => {
    const row = editedRows[idx];
    if (!row.invoice_number) return;
    setSavingIdx(idx);
    try {
      await saveInvoiceReview(row.invoice_number, row);
      setSavedIdx((prev) => [...new Set([...prev, idx])]);
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSavingIdx(null);
    }
  };

  const handleSaveAll = async () => {
    for (let i = 0; i < editedRows.length; i++) await handleSave(i);
    setStep(2);
  };

  // ── Actions ──────────────────────────────────────────────────────────────────
  const setAction = (key, name, val) =>
    setActions((p) => ({ ...p, [key]: { ...(p[key] || {}), [name]: val } }));

  const handlePushSap = async (key, row) => {
    setAction(key, 'sap', { loading: true });
    try {
      const d = await pushInvoiceToSap(row.invoice_number, row.ap_processor);
      // API may return 200 but still be a failure — detect it
      const isFailure = d?.success === false || d?.status === 'error' || /failed|error|not found/i.test(d?.message || '');
      if (isFailure) {
        setAction(key, 'sap', { error: d?.message || 'Push to SAP failed.' });
      } else {
        setAction(key, 'sap', { success: d?.message || 'Pushed to SAP successfully.' });
      }
    } catch (err) {
      setAction(key, 'sap', { error: err.message });
    }
  };

  const handleSendEmail = async (key, row) => {
    setAction(key, 'email', { loading: true });
    try {
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('user') || '';
      if (!userEmail) {
        setAction(key, 'email', { error: 'User email not found. Please login again.' });
        return;
      }
      const sap_payload_list = extractedRows.map((inv) => ({
        extracted_data: [inv],
        document_file_path: 'Uploaded via web interface',
        document_type: 'PDF',
      }));
      const res = await fetch('https://ocrtool.savictech.com/api/invoice/send_email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: userEmail, sap_payload_list }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.message || `HTTP ${res.status}`);
      setAction(key, 'email', { success: d?.message || `Email sent to ${userEmail}` });
    } catch (err) {
      setAction(key, 'email', { error: err.message });
    }
  };

  const handleExportExcel = async () => {
    try {
      const sap_payload_list = extractedRows.map((inv) => ({
        extracted_data: [inv],
        document_file_path: 'Uploaded via web interface',
        document_type: 'PDF',
      }));
      const res = await fetch('https://ocrtool.savictech.com/api/export-invoices-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sap_payload_list }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = res.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      link.setAttribute('download', match ? match[1].replace(/['"]/g, '') : 'SAP_Invoices.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  const handleReset = () => {
    setStep(0); setFiles([]); setExtractedRows([]); setEditedRows([]);
    setSavedIdx([]); setActions({}); setSummary(null); setUploadStage('idle');
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const curInv = extractedRows[activeInvIdx];
  const curEdit = editedRows[activeInvIdx];

  return (
    <div>
      {/* Step indicator */}
      <div className="inv-steps">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className={`inv-step${i < step ? ' done' : i === step ? ' active' : ''}`}>
              <div className="inv-step-circle">{i < step ? <FiCheckCircle size={14} /> : i + 1}</div>
              <span className="inv-step-label">{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`inv-step-line${i < step ? ' done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Upload status cards — shown after upload attempt */}
      {(extractedRows.length > 0 || uploadError) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {/* Uploaded */}
          <div style={{
            flex: 1, minWidth: 130, display: 'flex', alignItems: 'center', gap: 12,
            background: 'linear-gradient(135deg,#e0f2fe,#f0f9ff)',
            border: '1px solid #bae6fd', borderRadius: 12, padding: '12px 16px',
          }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FiUpload size={17} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Uploaded</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0c4a6e', lineHeight: 1.1 }}>{extractedRows.length}</div>
            </div>
          </div>

          {/* Failed */}
          {(() => {
            const uploadFailed = uploadError ? Math.max(0, files.length - extractedRows.length) : 0;
            const sapFailed = Object.values(actions).filter((a) => a.sap?.error).length;
            const totalFailed = uploadFailed + sapFailed;
            const hasError = totalFailed > 0;
            return (
              <div style={{
                flex: 1, minWidth: 130, display: 'flex', alignItems: 'center', gap: 12,
                background: hasError ? 'linear-gradient(135deg,#fee2e2,#fff1f2)' : 'linear-gradient(135deg,#f8fafc,#f1f5f9)',
                border: `1px solid ${hasError ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 12, padding: '12px 16px',
              }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: hasError ? '#b91c1c' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FiXCircle size={17} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: hasError ? '#b91c1c' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Failed</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: hasError ? '#7f1d1d' : '#334155', lineHeight: 1.1 }}>
                    {totalFailed}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Pushed to SAP */}
          <div style={{
            flex: 1, minWidth: 130, display: 'flex', alignItems: 'center', gap: 12,
            background: 'linear-gradient(135deg,#d1fae5,#f0fdf4)',
            border: '1px solid #6ee7b7', borderRadius: 12, padding: '12px 16px',
          }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: '#065f46', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FiSend size={17} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Pushed to SAP</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#064e3b', lineHeight: 1.1 }}>
                {Object.values(actions).filter((a) => a.sap?.success).length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 1: Upload ── */}
      {step === 0 && (
        <div className="inv-card">
          <div className="inv-card-title"><FiUpload size={16} /> Upload Invoices</div>

          <div
            className={`inv-drop-zone${dragOver ? ' drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileRef.current?.click()}
          >
            <div className="inv-drop-icon">
              <FiUpload size={26} />
            </div>
            <h4>{dragOver ? 'Release to upload' : 'Drag & drop invoices here'}</h4>
            <p>Click to browse · supports multiple files at once</p>
            <div className="inv-file-tags">
              {['PDF', 'PNG', 'JPG', 'XLSX'].map((t) => (
                <span key={t} className="inv-file-tag">{t}</span>
              ))}
            </div>
            <input
              ref={fileRef} type="file" multiple style={{ display: 'none' }}
              accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
              onChange={(e) => addFiles(e.target.files)}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--sub)' }}>
                  {files.length} file{files.length !== 1 ? 's' : ''} queued
                </span>
                <button className="inv-btn ghost" style={{ padding: '3px 10px', fontSize: '0.74rem' }}
                  onClick={() => setFiles([])}>
                  <FiX size={12} /> Clear all
                </button>
              </div>
              {files.map((f) => {
                const ext = f.name.split('.').pop();
                return (
                  <div key={f.name} className="inv-picked-file">
                    <div className="inv-picked-file-info">
                      <span className="inv-file-ext" style={{ background: fileColor(ext) }}>{ext.toUpperCase()}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.84rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>{formatBytes(f.size)}</div>
                      </div>
                    </div>
                    <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--red)', padding: 4 }}
                      onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}>
                      <FiX size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload stages */}
          {uploading && (
            <div className="inv-upload-stages" style={{ marginTop: 16 }}>
              {[
                { key: 'uploading',   label: 'Sending files…' },
                { key: 'processing',  label: 'Extracting data…' },
                { key: 'done',        label: 'Complete' },
              ].map((s, i) => {
                const isDone   = uploadStage === 'done' || (uploadStage === 'processing' && i === 0);
                const isActive = uploadStage === s.key;
                return (
                  <React.Fragment key={s.key}>
                    <div className={`inv-stage-dot${isActive ? ' active' : isDone ? ' done' : ''}`} />
                    <span style={{ fontSize: '0.8rem', color: isActive ? 'var(--blue-1)' : isDone ? 'var(--green)' : 'var(--muted)', fontWeight: isActive || isDone ? 600 : 400 }}>
                      {s.label}
                    </span>
                    {i < 2 && <span style={{ color: 'var(--border)', margin: '0 4px' }}>·</span>}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {uploadError && (
            <div className="inv-error-banner" style={{ marginTop: 12 }}>
              <FiAlertCircle size={14} /> {uploadError}
            </div>
          )}

          <div className="inv-btn-row">
            <button className="inv-btn primary" onClick={handleUpload} disabled={!files.length || uploading}>
              {uploading
                ? <><div className="inv-spinner" />Processing {files.length} file{files.length !== 1 ? 's' : ''}…</>
                : <><FiUpload size={14} />Extract {files.length > 0 ? `${files.length} Invoice${files.length !== 1 ? 's' : ''}` : 'Invoice Data'}</>}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Review & Edit ── */}
      {step === 1 && (
        <div>
          {/* Summary banner */}
          {summary && (
            <div className="inv-success-banner" style={{ marginBottom: 16 }}>
              <FiCheckCircle size={15} />
              <strong>Extraction complete</strong> —&nbsp;
              {summary.total_invoices_loaded ?? extractedRows.length} invoice{extractedRows.length !== 1 ? 's' : ''} extracted
              {summary.successful_pushed_invoices > 0 && `, ${summary.successful_pushed_invoices} pushed`}
            </div>
          )}

          {/* Invoice selector tabs */}
          {extractedRows.length > 1 && (
            <div className="inv-invoice-tabs">
              {extractedRows.map((inv, i) => {
                const label = inv['Invoice Number'] || inv['invoice_number'] || `Invoice ${i + 1}`;
                const isSaved = savedIdx.includes(i);
                return (
                  <button
                    key={inv._id}
                    className={`inv-invoice-tab-btn${activeInvIdx === i ? ' active' : ''}`}
                    onClick={() => setActiveInvIdx(i)}
                  >
                    {isSaved && <FiCheckCircle size={12} />}
                    {label}
                    <span className="inv-invoice-tab-badge">{i + 1}</span>
                  </button>
                );
              })}
            </div>
          )}

          {extractedRows.length > 0 && curInv ? (
            <>
              {/* Extracted data viewer */}
              <div className="inv-card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div className="inv-card-title" style={{ margin: 0 }}>
                    <FiFile size={15} /> Extracted Data
                  </div>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sub)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
                    onClick={() => toggleExpand(curInv._id)}
                  >
                    {expandedIds.has(curInv._id) ? <><FiChevronUp size={14} /> Collapse</> : <><FiChevronDown size={14} /> Expand</>}
                  </button>
                </div>

                {expandedIds.has(curInv._id) && (
                  <>
                    <div className="inv-extracted-grid">
                      {displayKeys(curInv).map((key) =>
                        curInv[key] !== undefined && curInv[key] !== '' && curInv[key] !== null ? (
                          <div key={key} className="inv-extracted-kv">
                            <div className="kv-label">{key}</div>
                            <div className="kv-value" title={String(curInv[key])}>{String(curInv[key])}</div>
                          </div>
                        ) : null
                      )}
                    </div>

                    {curInv._items?.length > 0 && (
                      <>
                        <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--sub)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8, marginTop: 8 }}>
                          Line Items ({curInv._items.length})
                        </div>
                        <div className="inv-table-wrap">
                          <table className="inv-table">
                            <thead>
                              <tr>{Object.keys(curInv._items[0]).map((k) => <th key={k}>{k}</th>)}</tr>
                            </thead>
                            <tbody>
                              {curInv._items.map((item, i) => (
                                <tr key={i} style={{ '--row-delay': `${i * 25}ms` }}>
                                  {Object.values(item).map((v, j) => <td key={j}>{String(v ?? '')}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Edit card */}
              <div className="inv-edit-card" style={{ marginBottom: 16 }}>
                <div className="inv-edit-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FiEdit3 size={15} style={{ color: 'var(--blue-1)' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--ink)' }}>
                      Edit Key Fields
                    </span>
                    {savedIdx.includes(activeInvIdx) && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--green)', fontWeight: 600 }}>
                        <FiCheckCircle size={12} /> Saved
                      </span>
                    )}
                  </div>
                  <button
                    className={`inv-btn ${savedIdx.includes(activeInvIdx) ? 'success' : 'primary'}`}
                    style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                    onClick={() => handleSave(activeInvIdx)}
                    disabled={savingIdx === activeInvIdx || !curEdit?.invoice_number}
                  >
                    {savingIdx === activeInvIdx
                      ? <><div className="inv-spinner" />Saving…</>
                      : savedIdx.includes(activeInvIdx)
                        ? <><FiCheckCircle size={12} />Saved</>
                        : 'Save'}
                  </button>
                </div>

                {curEdit && (
                  <div className="inv-edit-fields">
                    {[
                      { field: 'invoice_number',        label: 'Invoice Number',   type: 'text',   placeholder: 'e.g. INV-2024-001' },
                      { field: 'invoice_date',          label: 'Invoice Date',     type: 'date',   placeholder: '' },
                      { field: 'invoice_total',         label: 'Total Amount',     type: 'text',   placeholder: 'e.g. ₹12,500' },
                      { field: 'purchase_order_number', label: 'PO Number',        type: 'text',   placeholder: 'e.g. PO-456' },
                      { field: 'ap_processor',          label: 'AP Processor',     type: 'email',  placeholder: 'user@company.com' },
                    ].map(({ field, label, type, placeholder }) => (
                      <div key={field} className="inv-edit-field">
                        <label>{label}</label>
                        <input
                          type={type} value={curEdit[field]}
                          onChange={(e) => updateField(activeInvIdx, field, e.target.value)}
                          placeholder={placeholder}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="inv-card">
              <div className="inv-empty"><p>No invoice data was extracted. Try uploading again.</p></div>
            </div>
          )}

          <div className="inv-btn-row">
            <button className="inv-btn ghost" onClick={() => setStep(0)}><FiUpload size={13} /> Back</button>
            {extractedRows.length > 1 && (
              <button className="inv-btn secondary" onClick={handleSaveAll} disabled={editedRows.length === 0}>
                Save All & Continue →
              </button>
            )}
            <button className="inv-btn primary" onClick={() => setStep(2)}>
              Continue to Actions →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Actions ── */}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--sub)', fontWeight: 500 }}>
              {editedRows.length} invoice{editedRows.length !== 1 ? 's' : ''} ready for actions
            </div>
            <button className="inv-btn ghost" style={{ gap: 6 }} onClick={() => handleExportExcel()}>
              <FiDownload size={14} /> Export All to Excel
            </button>
          </div>

          {editedRows.map((row, idx) => {
            const key = row.invoice_number || `inv-${idx}`;
            const act = actions[key] || {};
            const allDone = act.sap?.success && act.email?.success;
            return (
              <div key={idx} className="inv-card" style={{ marginBottom: 16 }}>
                {/* Invoice header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                  <div style={{
                    background: 'linear-gradient(135deg,var(--blue-1),#1976d2)',
                    color: '#fff', borderRadius: 'var(--r-md)', padding: '6px 14px',
                    fontWeight: 800, fontSize: '0.9rem', letterSpacing: '-0.01em',
                  }}>
                    {key}
                  </div>
                  {row.invoice_date  && <span style={{ fontSize: '0.8rem', color: 'var(--sub)' }}>{row.invoice_date}</span>}
                  {row.invoice_total && <span style={{ fontSize: '0.8rem', color: 'var(--sub)' }}>Total: {row.invoice_total}</span>}
                  {row.purchase_order_number && <span style={{ fontSize: '0.8rem', color: 'var(--sub)' }}>PO: {row.purchase_order_number}</span>}
                  {allDone && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', color: 'var(--green)', fontWeight: 700, fontSize: '0.82rem' }}>
                      <FiCheckCircle size={14} /> All actions complete
                    </span>
                  )}
                </div>

                <div className="inv-action-grid">
                  {/* Push to SAP */}
                  <div className="inv-action-card sap">
                    <div className="inv-action-icon sap"><FiZap size={22} /></div>
                    <div>
                      <h4>Push to SAP</h4>
                      <p>Submit this invoice for processing in the SAP system via the VIM workflow.</p>
                    </div>
                    {act.sap?.success && <div className="inv-action-result success"><FiCheckCircle size={12} /> {act.sap.success}</div>}
                    {act.sap?.error   && <div className="inv-action-result error"><FiAlertCircle size={12} /> {act.sap.error}</div>}
                    <button className="inv-btn primary" onClick={() => handlePushSap(key, row)}
                      disabled={act.sap?.loading || !!act.sap?.success}>
                      {act.sap?.loading ? <><div className="inv-spinner" />Pushing…</>
                        : act.sap?.success ? <><FiCheckCircle size={13} />Pushed</>
                        : <><FiZap size={13} />Push to SAP</>}
                    </button>
                  </div>

                  {/* Send Email */}
                  <div className="inv-action-card email">
                    <div className="inv-action-icon email"><FiMail size={22} /></div>
                    <div>
                      <h4>Send Email</h4>
                      <p>Email the extracted invoice data to the AP team for approval.</p>
                    </div>
                    {act.email?.success && <div className="inv-action-result success"><FiCheckCircle size={12} /> {act.email.success}</div>}
                    {act.email?.error   && <div className="inv-action-result error"><FiAlertCircle size={12} /> {act.email.error}</div>}
                    <button className="inv-btn success" onClick={() => handleSendEmail(key, row)}
                      disabled={act.email?.loading || !!act.email?.success}>
                      {act.email?.loading ? <><div className="inv-spinner" />Sending…</>
                        : act.email?.success ? <><FiCheckCircle size={13} />Sent</>
                        : <><FiMail size={13} />Send Email</>}
                    </button>
                  </div>

                  {/* Export */}
                  <div className="inv-action-card export">
                    <div className="inv-action-icon export"><FiDownload size={22} /></div>
                    <div>
                      <h4>Export to Excel</h4>
                      <p>Download all extracted invoice data as a formatted Excel spreadsheet.</p>
                    </div>
                    <button className="inv-btn ghost" onClick={() => handleExportExcel()}>
                      <FiDownload size={13} /> Export Excel
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="inv-btn-row">
            <button className="inv-btn ghost" onClick={() => setStep(1)}>← Back to Review</button>
            <button className="inv-btn secondary" onClick={handleReset}>
              <FiRefreshCw size={14} /> Upload Another Invoice
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCRProcess;
